import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Phone } from 'lucide-react';
import api from '../services/api';

/**
 * Gemaskeerd bellen vanuit een gesprek (5.4). Het platform belt eerst u op uw
 * eigen nummer en verbindt dan door; geen van beiden ziet het nummer van de ander.
 * Onzichtbaar als bellen op de server uit staat; uitgeschakeld met uitleg als het
 * in dit gesprek niet kan (geen nummer, groepsgesprek, …).
 */
interface BelStatus {
  beschikbaar: boolean;
  kan_bellen: boolean;
  reden: string | null;
  eigen_nummer_eindigt_op?: string;
}

export const BelKnop: React.FC<{ conversationId: string }> = ({ conversationId }) => {
  const { t } = useTranslation('common');
  const { data: status } = useQuery({
    queryKey: ['telefonie-status', conversationId],
    queryFn: async () => (await api.get('/telefonie/status', { params: { conversation_id: conversationId } })).data.data as BelStatus,
    staleTime: 60_000,
  });
  const bel = useMutation({
    mutationFn: async () => (await api.post('/telefonie/bel', { conversation_id: conversationId })).data.data as { eigen_nummer_eindigt_op: string },
    onSuccess: (r) => toast.success(t('bellen.wordtGebeld', { cijfers: r.eigen_nummer_eindigt_op }), { duration: 8000 }),
    onError: (e: any) => toast.error(e?.response?.data?.message || t('bellen.mislukt')),
  });

  if (!status?.beschikbaar) return null;

  const uitleg = status.kan_bellen ? t('bellen.uitleg', { cijfers: status.eigen_nummer_eindigt_op ?? '' }) : status.reden ?? '';
  return (
    <button
      type="button"
      onClick={() => bel.mutate()}
      disabled={!status.kan_bellen || bel.isPending}
      title={uitleg}
      aria-label={t('bellen.knop')}
      className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Phone className="w-4 h-4" aria-hidden />
      {bel.isPending ? t('bellen.bezig') : t('bellen.knop')}
    </button>
  );
};

export default BelKnop;
